import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';

import { CS_FONT, CS_WEIGHT, CS_Z, csTokens } from 'components/CallScribe/callScribeTheme';

const JUMP_BAR_PAD = 10;
const JUMP_LINK_HEIGHT = 32;
const JUMP_BAR_HEIGHT = JUMP_BAR_PAD * 2 + JUMP_LINK_HEIGHT;

const useCallDetailDrawerStyles = makeStyles((theme: Theme) => {
  const t = csTokens(theme);
  return {
    paper: {
      background: t.bg,
      color: t.text,
      fontFamily: "'Nunito', sans-serif",
      backgroundImage: 'none',
    },
    content: {
      position: 'relative',
      isolation: 'isolate',
      display: 'flex',
      flexDirection: 'column',
      boxSizing: 'border-box',
      height: '100%',
      overflowY: 'auto',
      overscrollBehavior: 'contain',
      '& > *': { flexShrink: 0 },
    },
    header: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: 12,
      padding: '16px 8px 4px 16px',
      minWidth: 0,
    },
    headerBody: {
      flex: '1 1 auto',
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      minWidth: 0,
    },
    headerTitle: {
      margin: 0,
      fontSize: 20,
      lineHeight: '26px',
      fontWeight: CS_WEIGHT.heavy,
      letterSpacing: '-0.01em',
      color: t.text,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    headerMeta: {
      ...CS_FONT.small,
      margin: 0,
      color: t.text2,
      fontVariantNumeric: 'tabular-nums',
    },
    closeButton: {
      padding: 0,
      width: 44,
    },
    chips: {
      padding: '8px 16px 0',
    },
    jump: {
      position: 'sticky',
      top: 0,
      zIndex: CS_Z.sticky,
      display: 'flex',
      gap: 6,
      padding: `${JUMP_BAR_PAD}px 16px`,
      background: t.bg,
      overflowX: 'auto',
      scrollbarWidth: 'none',
      '&::-webkit-scrollbar': { display: 'none' },
    },
    jumpLink: {
      flex: '0 0 auto',
      minHeight: JUMP_LINK_HEIGHT,
      padding: '0 12px',
      borderRadius: 16,
      border: 'none',
      boxShadow: `inset 0 0 0 1px ${t.controlLine}`,
      background: 'transparent',
      color: t.text2,
      fontFamily: 'inherit',
      ...CS_FONT.small,
      fontWeight: CS_WEIGHT.strong,
      cursor: 'pointer',
      transition: 'background-color 150ms ease-out, color 150ms ease-out',
      '&:hover': { background: t.surface3, color: t.text },
      '&:focus-visible': { outline: `2px solid ${t.focus}`, outlineOffset: 2 },
      '&[aria-current="location"]': {
        background: t.text,
        color: t.surface,
        boxShadow: 'none',
      },
    },
    sections: {
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
      padding: '2px 16px 24px',
    },
    jumpTarget: {
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
      scrollMarginTop: `${JUMP_BAR_HEIGHT + 4}px`,
      outline: 'none',
    },
    noRecording: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      minHeight: 44,
      padding: '10px 14px',
      borderRadius: 14,
      background: t.surface,
      border: `1px solid ${t.line}`,
      ...CS_FONT.small,
      color: t.text2,
      '& svg': { fontSize: 20, flex: '0 0 auto' },
    },
    inlineNote: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: 8,
      color: t.text2,
      '& svg': { fontSize: 18, flex: '0 0 auto', marginTop: 1 },
    },
    sectionBody: {
      ...CS_FONT.small,
      whiteSpace: 'pre-wrap',
      color: t.text,
      '& strong': { fontWeight: CS_WEIGHT.heavy },
    },
    errorText: {
      ...CS_FONT.small,
      color: t.neg,
    },
    inlineRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      flexWrap: 'wrap',
    },
    ticketId: {
      userSelect: 'all',
      fontVariantNumeric: 'tabular-nums',
    },
    copyField: {
      ...CS_FONT.small,
      padding: '4px 8px',
      borderRadius: 8,
      border: `1px solid ${t.fieldLine}`,
      background: t.surface,
      color: t.text,
      '&:focus-visible': { outline: `2px solid ${t.focus}`, outlineOffset: 2 },
    },
    actionItems: {
      margin: 0,
      paddingLeft: 18,
      ...CS_FONT.small,
      color: t.text,
      '& li + li': { marginTop: 4 },
    },
    actionItemMeta: {
      color: t.text2,
    },
    tagRow: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 6,
    },
    pipelineSpinner: {
      color: t.text2,
    },
  };
});

export default useCallDetailDrawerStyles;
